import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

type LocalFormData = {
  nome: string;
  descricao: string;
  tipo_local_id: string;
  cliente_id: string;
  id_empresa: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  frete_moto: number;
  frete_carro_hatch: number;
  frete_utilitario: number;
};

type TipoLocal = {
  id: string;
  nome: string;
};

type Cliente = {
  id: string;
  nome: string;
};

type Empresa = {
  id: string;
  razao_social: string;
};

interface LocalModalProps {
  onLocalCriado?: () => void;
  trigger?: React.ReactNode;
}

export default function LocalModal({ onLocalCriado, trigger }: LocalModalProps) {
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tiposLocal, setTiposLocal] = useState<TipoLocal[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);

  const canWrite = hasPermission('locais', 'locais', 'write');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<LocalFormData>();

  useEffect(() => {
    if (open) {
      loadOptions();
    }
  }, [open]);

  const loadOptions = async () => {
    try {
      // Carregar tipos de local
      // @ts-ignore - Temporary fix for Supabase type inference issue
      const { data: tiposData }: { data: any } = await supabase
        .from('tipo_local')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (tiposData) setTiposLocal(tiposData);

      // Carregar clientes
      // @ts-ignore - Temporary fix for deep recursion type error
      const { data: clientesData } = await supabase
        .from('clientes')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (clientesData) setClientes(clientesData);

      // Carregar empresas
      // @ts-ignore - Temporary fix for deep recursion type error
      const { data: empresasData } = await supabase
        .from('empresas')
        .select('id, razao_social')
        .eq('ativo', true)
        .order('razao_social');

      if (empresasData) setEmpresas(empresasData);

    } catch (error: any) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  const onSubmit = async (data: LocalFormData) => {
    if (!canWrite) {
      toast({
        title: "Erro",
        description: "Você não tem permissão para criar locais.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const localData = {
        ...data,
        cliente_id: data.cliente_id || null,
        id_empresa: data.id_empresa || null,
        frete_moto: data.frete_moto || null,
        frete_carro_hatch: data.frete_carro_hatch || null,
        frete_utilitario: data.frete_utilitario || null,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      };

      const { error } = await supabase
        .from('locais')
        .insert([localData]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Local criado com sucesso!",
      });

      reset();
      setOpen(false);
      
      if (onLocalCriado) {
        onLocalCriado();
      }

    } catch (error: any) {
      console.error('Erro ao criar local:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar local: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canWrite) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Novo Local
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Local</DialogTitle>
          <DialogDescription>
            Cadastre um novo local no sistema
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                {...register('nome', { required: 'Nome é obrigatório' })}
                placeholder="Nome do local"
              />
              {errors.nome && (
                <p className="text-sm text-destructive">{errors.nome.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo_local_id">Tipo de Local *</Label>
              <Select 
                value={watch('tipo_local_id')} 
                onValueChange={(value) => setValue('tipo_local_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposLocal.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tipo_local_id && (
                <p className="text-sm text-destructive">Tipo de local é obrigatório</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              {...register('descricao')}
              placeholder="Descrição do local"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cliente_id">Cliente (opcional)</Label>
              <Select 
                value={watch('cliente_id') || undefined} 
                onValueChange={(value) => setValue('cliente_id', value || '')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_empresa">Empresa (opcional)</Label>
              <Select 
                value={watch('id_empresa') || undefined} 
                onValueChange={(value) => setValue('id_empresa', value || '')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold">Endereço</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cep">CEP</Label>
                <Input
                  id="cep"
                  {...register('cep')}
                  placeholder="00000-000"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="logradouro">Logradouro</Label>
                <Input
                  id="logradouro"
                  {...register('logradouro')}
                  placeholder="Rua, Avenida, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  {...register('numero')}
                  placeholder="123"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="complemento">Complemento</Label>
                <Input
                  id="complemento"
                  {...register('complemento')}
                  placeholder="Apartamento, Sala, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  {...register('bairro')}
                  placeholder="Nome do bairro"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  {...register('cidade')}
                  placeholder="Nome da cidade"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="estado">Estado</Label>
                <Input
                  id="estado"
                  {...register('estado')}
                  placeholder="UF"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold">Valores de Frete</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frete_moto">Frete Moto</Label>
                <Input
                  id="frete_moto"
                  type="number"
                  step="0.01"
                  {...register('frete_moto', { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="frete_carro_hatch">Frete Carro Hatch</Label>
                <Input
                  id="frete_carro_hatch"
                  type="number"
                  step="0.01"
                  {...register('frete_carro_hatch', { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="frete_utilitario">Frete Utilitário</Label>
                <Input
                  id="frete_utilitario"
                  type="number"
                  step="0.01"
                  {...register('frete_utilitario', { valueAsNumber: true })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : "Criar Local"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}